import { Box, Stack, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { first } from 'lodash';
import { useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { Divider } from '@/components/Divider';
import {
  RadioGroupOption,
  SelectInputOption,
} from '@/components/form/baseInputs/inputTypes';
import { RadioGroup } from '@/components/form/baseInputs/RadioGroup';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormAwareButtonGroup } from '@/components/form/formAwareInputs/FormAwareButtonGroup';
import { FormAwareCurrencyInput } from '@/components/form/formAwareInputs/FormAwareCurrencyInput';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwarePercentInput } from '@/components/form/formAwareInputs/FormAwarePercentInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { FormAwareTextInput } from '@/components/form/formAwareInputs/FormAwareTextInput';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { AddNewAnythingModal } from '@/modules/common/AddNewAnythingModal/AddNewAnythingModal';
import { COLORS } from '@/styles/tokens/colors';
import { createRadioGroupOptionsFromEnum } from '@/utils/createOptionsFromEnum';
import { sumDecimalJS } from '@/utils/decimalJSUtils';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';
import { getNodes } from '@/utils/graphqlUtils';

import {
  BOTH_CLIENTS_SENTINEL,
  CashflowDrawerFormPaths,
  CashflowDrawerFormShape,
  CashflowEndType,
  CashflowFrequency,
  CashflowIncomeOrExpense,
  CashflowValueType,
} from './CashflowDrawer.types';
import { getTargetType, TargetType } from './CashflowDrawer.utils';
import { useEntityTargets } from './hooks/useEntityTargets';

export interface HouseholdCashflowComponentSharedProps {
  householdId: string | null;
  recipient?: string;
  /** Keep eMoney-owned values read-only while the target entity stays editable. */
  disableImportedFields?: boolean;
}
export function CashflowDrawerBody({
  householdId,
  recipient,
  disableImportedFields = false,
}: HouseholdCashflowComponentSharedProps) {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const [frequency] = useWatch({
    control,
    name: ['frequency'],
  });
  const body = (
    <Stack width="100%">
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <Name />
        </FormLayoutItem>
        <FormLayoutItem width={5}>
          <Stack height="100%" pb={1} justifyContent="end">
            <IncomeExpenseSelector />
          </Stack>
        </FormLayoutItem>
      </FormLayoutRow>
      {!recipient && (
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <Entity
              householdId={householdId}
              forceEnabled={disableImportedFields}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}
      {householdId && <CashflowRecipientWarning householdId={householdId} />}
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <AmountSection householdId={householdId} />
        </FormLayoutItem>
      </FormLayoutRow>
      <FormLayoutRow>
        <FormLayoutItem width={12}>
          <Card variant="filled" sx={{ p: 2 }}>
            <Stack spacing="20px">
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="title-s">Cash flow recurrence</Typography>
                <Frequency />
              </Stack>
              <Divider
                sx={{
                  borderColor: COLORS.GRAY[300],
                }}
              />
              {frequency === CashflowFrequency.Annual && (
                <Stack direction="row" spacing={2}>
                  <Box display="flex" sx={{ maxWidth: 80 }}>
                    <StartYear />
                  </Box>
                  <EndOnSelector householdId={householdId} />
                  <Box display="flex" sx={{ maxWidth: 120 }}>
                    <GrowthRate />
                  </Box>
                </Stack>
              )}
              {frequency === CashflowFrequency.OneTime && (
                <FormLayoutRow>
                  <FormLayoutItem width={12}>
                    <Box display="flex" sx={{ maxWidth: 80 }}>
                      <StartYear />
                    </Box>
                  </FormLayoutItem>
                </FormLayoutRow>
              )}
            </Stack>
          </Card>
        </FormLayoutItem>
      </FormLayoutRow>
      {householdId && <CashFlowContinuityWarning householdId={householdId} />}
    </Stack>
  );

  if (!disableImportedFields) return body;

  return (
    <FormFieldsDisabledProvider isDisabled>{body}</FormFieldsDisabledProvider>
  );
}

function Name() {
  const { control } = useFormContext<CashflowDrawerFormShape>();

  return (
    <FormAwareTextInput<CashflowDrawerFormShape>
      control={control}
      fieldName={'name' as const satisfies CashflowDrawerFormPaths}
      label="Name"
      required
    />
  );
}

function Entity({
  householdId,
  forceEnabled = false,
}: HouseholdCashflowComponentSharedProps & { forceEnabled?: boolean }) {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const [options] = useEntityTargets(householdId);
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState<boolean>();

  const input = (
    <>
      <FormAwareTypeaheadSelectInput<CashflowDrawerFormShape>
        control={control}
        label="Entity"
        placeholder="Search entities"
        options={options}
        groupBy={(option) => option.groupName ?? ''}
        fieldName={'targetId' as const satisfies CashflowDrawerFormPaths}
        required
        addNewOption={{
          onClick: () => openModal(),
        }}
      />
      {householdId && (
        <AddNewAnythingModal
          isOpen={isModalOpen}
          handleClose={() => closeModal()}
          householdId={householdId}
          addableTypes={['entity']}
          onAfterCreate={() => closeModal()}
        />
      )}
    </>
  );

  if (!forceEnabled) return input;

  return (
    <FormFieldsDisabledProvider
      isDisabled={false}
      inheritParentDisabled={false}
    >
      {input}
    </FormFieldsDisabledProvider>
  );
}

function IncomeExpenseSelector() {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const options = useMemo<RadioGroupOption<string>[]>(
    () => [
      {
        value: CashflowIncomeOrExpense.Income,
        label: 'Income',
      },
      {
        value: CashflowIncomeOrExpense.Expense,
        label: 'Expense',
      },
    ],
    []
  );

  return (
    <FormAwareRadioGroup<CashflowDrawerFormShape>
      control={control}
      fieldName={'incomeOrExpense' as const satisfies CashflowDrawerFormPaths}
      options={options}
      row
    />
  );
}

function AmountSection({ householdId }: HouseholdCashflowComponentSharedProps) {
  const { control, setValue } = useFormContext<CashflowDrawerFormShape>();
  const [valueType, targetId, percentage, incomeOrExpense] = useWatch({
    control,
    name: ['valueType', 'targetId', 'percentage', 'incomeOrExpense'],
  });

  // Calculate the maximum transfer amount for the entity
  const [_, { data: queryData }] = useEntityTargets(householdId);
  const household = first(getNodes(queryData?.households));
  const entities = getNodes(household?.entities);
  const selectedEntity = entities?.find((e) => e.id === targetId);
  const selectedClient = household?.possiblePrimaryClients?.find(
    (c) => c.id === targetId
  );

  const maxTransfer = useMemo(() => {
    if (selectedEntity?.subtype) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const subtypeWithValue = selectedEntity.subtype as any;
      if ('currentValue' in subtypeWithValue) {
        const value = subtypeWithValue.currentValue;
        return value instanceof Decimal ? value : new Decimal(0);
      }
    }

    if (selectedClient) {
      return sumDecimalJS(
        selectedClient.ownedOwnershipStakes?.map((s) => s.ownedValue) ?? []
      );
    }

    return new Decimal(0);
  }, [selectedEntity, selectedClient]);

  // Calculate dollar amount from percentage
  const calculatedAmount = useMemo(() => {
    if (
      valueType === CashflowValueType.Percent &&
      percentage &&
      maxTransfer.greaterThan(0)
    ) {
      return maxTransfer.times(percentage.dividedBy(100));
    }
    return null;
  }, [valueType, percentage, maxTransfer]);

  const isMaxTransferZero = maxTransfer.equals(0);
  const isExpense = incomeOrExpense === CashflowIncomeOrExpense.Expense;

  const helpText = useMemo(() => {
    if (isMaxTransferZero && isExpense && (selectedClient || selectedEntity)) {
      return 'Available for transfer: $0';
    }

    if (
      valueType === CashflowValueType.Percent &&
      calculatedAmount &&
      isExpense
    ) {
      return `Transfer amount: ${formatCurrencyNoDecimals(calculatedAmount)}`;
    }
    if (targetId && maxTransfer.greaterThan(0) && isExpense) {
      return `Max transfer: ${formatCurrencyNoDecimals(maxTransfer)} (based on gross value of entity, exclusive of liabilities, receivables and cash value of insurance)`;
    }
    return undefined;
  }, [
    isMaxTransferZero,
    isExpense,
    selectedClient,
    selectedEntity,
    valueType,
    calculatedAmount,
    targetId,
    maxTransfer,
  ]);

  // Disable percentage for income cash flows on individuals
  const isClientProfile = getTargetType(targetId) === TargetType.ClientProfile;
  const isIncomeForIndividual =
    isClientProfile && incomeOrExpense === CashflowIncomeOrExpense.Income;

  useEffect(() => {
    if (isIncomeForIndividual) {
      setValue('valueType', CashflowValueType.Dollar);
    }
  }, [isIncomeForIndividual, setValue]);

  return (
    <Stack spacing={1} direction="row">
      {!isIncomeForIndividual && (
        <Box sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
          <FormAwareButtonGroup<CashflowDrawerFormShape>
            control={control}
            fieldName={'valueType' as const satisfies CashflowDrawerFormPaths}
            label="After-tax amount"
            required
            variant="light"
            options={[
              {
                value: CashflowValueType.Dollar,
                display: 'Fixed $ amount',
              },
              {
                value: CashflowValueType.Percent,
                display: '% of entity value',
              },
            ]}
          />
        </Box>
      )}
      <Box pt={isIncomeForIndividual ? 0 : 2.5}>
        {valueType === CashflowValueType.Dollar ? (
          <FormAwareCurrencyInput<CashflowDrawerFormShape>
            control={control}
            fieldName={'amount' as const satisfies CashflowDrawerFormPaths}
            label="After-tax amount"
            required
            isDecimalJSInput
            helpText={helpText}
            hideLabel={!isIncomeForIndividual}
            helpTextVariant={isMaxTransferZero ? 'warning' : undefined}
          />
        ) : (
          <FormAwarePercentInput<CashflowDrawerFormShape>
            control={control}
            fieldName={'percentage' as const satisfies CashflowDrawerFormPaths}
            label="Percentage"
            required
            isDecimalJSInput
            decimalScale={2}
            helpText={helpText}
            hideLabel
            helpTextVariant={isMaxTransferZero ? 'warning' : undefined}
          />
        )}
      </Box>
    </Stack>
  );
}

function Frequency() {
  const { control, setValue } = useFormContext<CashflowDrawerFormShape>();
  const { disabled } = useFormFieldsDisabled();
  const baseOptions = createRadioGroupOptionsFromEnum(CashflowFrequency);

  return (
    <Controller
      control={control}
      name="frequency"
      render={({ field }) => (
        <RadioGroup
          name={field.name}
          inputRef={field.ref}
          value={field.value}
          onBlur={field.onBlur}
          onChange={(_event, value) => {
            if (value === CashflowFrequency.Annual) {
              setValue('endYear', null, { shouldDirty: true });
            }
            field.onChange(value);
          }}
          options={baseOptions}
          disabled={disabled}
          row
        />
      )}
    />
  );
}

function StartYear() {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const [frequency] = useWatch({
    control,
    name: ['frequency'],
  });

  return (
    <FormAwareFormattedNumberInput<CashflowDrawerFormShape>
      control={control}
      fieldName={'startYear' as const satisfies CashflowDrawerFormPaths}
      label={frequency === CashflowFrequency.OneTime ? 'Year' : 'Start year'}
      required
    />
  );
}

function EndOnSelector({ householdId }: HouseholdCashflowComponentSharedProps) {
  const { control, setValue } = useFormContext<CashflowDrawerFormShape>();
  const [targetId, endType] = useWatch({
    control,
    name: ['targetId', 'endType'],
  });

  const isClientProfile =
    targetId.toLowerCase().startsWith('cp_') ||
    targetId === BOTH_CLIENTS_SENTINEL;
  const isEntity = targetId.toLowerCase().startsWith('entity_');

  const [_, { data: queryData }] = useEntityTargets(householdId);
  const { possiblePrimaryClients = [] } =
    first(getNodes(queryData?.households)) || {};

  // Build options based on target type
  const endOnOptions = useMemo<SelectInputOption<string>[]>(() => {
    const options: SelectInputOption<string>[] = [
      { value: CashflowEndType.Year, display: 'Year' },
    ];

    if (isEntity) {
      options.push({
        value: CashflowEndType.TrustTermination,
        display: 'Entity termination',
      });
    }

    if (isClientProfile) {
      // Find the selected client
      const selectedClient = possiblePrimaryClients.find(
        (c) => c.id === targetId
      );
      if (selectedClient) {
        options.push({
          value: CashflowEndType.Death,
          display: `Upon ${selectedClient.firstName}'s death`,
        });

        const otherClient = possiblePrimaryClients.find(
          (c) => c.id !== targetId
        );

        if (otherClient) {
          options.push({
            value: CashflowEndType.DeathOther,
            display: `Upon ${otherClient.firstName}'s death`,
          });
        }

        options.push({
          value: CashflowEndType.BothDeaths,
          display: 'Upon both deaths',
        });
      }
    }

    return options;
  }, [isEntity, isClientProfile, targetId, possiblePrimaryClients]);

  // When endType changes, handle the related fields
  useEffect(() => {
    const otherClient = possiblePrimaryClients.find((c) => c.id !== targetId);

    if (endType === CashflowEndType.Death && isClientProfile) {
      setValue('endAtDeathClientProfileId', targetId);
    }
    if (
      endType === CashflowEndType.DeathOther &&
      isClientProfile &&
      otherClient
    ) {
      setValue('endAtDeathClientProfileId', otherClient.id);
    }
    if (endType === CashflowEndType.BothDeaths) {
      setValue('endAtDeathClientProfileId', CashflowEndType.BothDeaths);
    }
  }, [endType, targetId, isClientProfile, setValue, possiblePrimaryClients]);

  return (
    <Stack direction="row" spacing={2}>
      <Box width={endType === CashflowEndType.Year ? 168 : 256}>
        <FormAwareSelectInput<CashflowDrawerFormShape>
          control={control}
          label="Ends on"
          options={endOnOptions}
          showEmptyValue={false}
          fieldName={'endType' as const satisfies CashflowDrawerFormPaths}
          required
        />
      </Box>

      {endType === CashflowEndType.Year && (
        <Box sx={{ width: 80 }}>
          <FormAwareFormattedNumberInput<CashflowDrawerFormShape>
            control={control}
            fieldName={'endYear' as const satisfies CashflowDrawerFormPaths}
            label="End year"
            required
          />
        </Box>
      )}
    </Stack>
  );
}

function GrowthRate() {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const [valueType] = useWatch({
    control,
    name: ['valueType'],
  });

  // Disable growth rate for income with percentage
  const isDisabled = valueType === CashflowValueType.Percent;

  if (isDisabled) {
    return null;
  }

  return (
    <FormAwarePercentInput<CashflowDrawerFormShape>
      isDecimalJSInput
      control={control}
      fieldName={'growthRate' as const satisfies CashflowDrawerFormPaths}
      label="Growth rate"
      required
      fixedDecimalScale
      decimalScale={2}
    />
  );
}

export function CashflowRecipientWarning({
  householdId,
}: {
  householdId: string;
}) {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const [targetId, incomeOrExpense] = useWatch({
    control,
    name: ['targetId', 'incomeOrExpense'],
  });

  const [_, { data: queryData }] = useEntityTargets(householdId);
  const household = first(getNodes(queryData?.households));
  const entities = getNodes(household?.entities);
  const selectedEntity = entities?.find((e) => e.id === targetId);
  const selectedClient = household?.possiblePrimaryClients?.find(
    (c) => c.id === targetId
  );

  const selectedItemValueIsZero = useMemo(() => {
    if (selectedEntity?.subtype) {
      const subtypeWithValue = selectedEntity.subtype;
      if ('currentValue' in subtypeWithValue) {
        const value = subtypeWithValue.currentValue;
        return value.equals(0);
      }
    }

    if (selectedClient) {
      return sumDecimalJS(
        selectedClient.ownedOwnershipStakes?.map((s) => s.ownedValue) ?? []
      ).equals(0);
    }

    return false;
  }, [selectedClient, selectedEntity?.subtype]);

  const name = selectedClient?.firstName ?? selectedEntity?.subtype.displayName;

  if (
    !selectedItemValueIsZero ||
    !name ||
    incomeOrExpense === CashflowIncomeOrExpense.Income
  ) {
    return null;
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem width={12}>
        <Callout severity="warning">
          {`${name} has $0 available for expenses. Consider selecting a different entity.`}
        </Callout>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}

export function CashFlowContinuityWarning({
  householdId,
}: {
  householdId: string;
}) {
  const { control } = useFormContext<CashflowDrawerFormShape>();
  const [targetId, frequency, endType, endAtDeathClientProfileId] = useWatch({
    control,
    name: ['targetId', 'frequency', 'endType', 'endAtDeathClientProfileId'],
  });

  const [_, { data: queryData }] = useEntityTargets(householdId);
  const household = first(getNodes(queryData?.households));
  const selectedClient = household?.possiblePrimaryClients?.find(
    (c) => c.id === targetId
  );

  const otherClient = household?.possiblePrimaryClients?.find(
    (c) => c.id !== targetId
  );

  if (
    !selectedClient ||
    frequency === CashflowFrequency.OneTime ||
    !otherClient ||
    (endType === CashflowEndType.Death &&
      endAtDeathClientProfileId === selectedClient.id)
  ) {
    return null;
  }

  return (
    <FormLayoutRow>
      <FormLayoutItem width={12}>
        <Callout severity="warning">
          {`Cash flow will be transferred to ${otherClient.displayName} upon ${selectedClient.firstName}’s death if ${selectedClient.firstName} predeceases ${otherClient.firstName}.`}
        </Callout>
      </FormLayoutItem>
    </FormLayoutRow>
  );
}
